import {
  type FormatOptions,
  type FormatResult,
  type LintOptions,
  type LintReport,
  type RepairOptions,
  type RepairResult,
  utf8ByteLength,
} from '@jsonfmt/core';

import type { AnalyzerService } from './types';
import { createAnalysisCache } from './worker/analysisCache';
import { handleRequest } from './worker/analyze.worker';
import type { DocumentVersion, Seq, WorkerRequest, WorkerResponse } from './worker/protocol';

/**
 * `AnalyzerService`: one scheduler, two transports (ADR-0009, ADR-0010).
 *
 * ═══ WHY THIS FILE OWNS SCHEDULING AT ALL ═══
 *
 * CodeMirror's `linter()` facet is not used. Its trailing debounce would MULTIPLY with ours,
 * and its `batchResults` flushes 200 ms after the first source resolves — which silently
 * drops a Worker answer that arrives later (ADR-0009, measured against 6.9.7). The same
 * analysis feeds three consumers (editor underlines, the header pill, the problems panel),
 * so it belongs to nobody's widget.
 *
 * ═══ THE THREE RULES ═══
 *
 * 1. **Transport by size.** At or above `workerThresholdBytes` (256 KiB, ADR-0010) a request
 *    goes to the Worker; below it, straight through `handleRequest` on this thread, because
 *    a 3 ms analysis is cheaper than the round trip that would schedule it. Both transports
 *    run the SAME `handleRequest` over the SAME `AnalysisCache`; `analyzer.contract.test.ts`
 *    drives one fixture set through both and requires identical output.
 *
 * 2. **One in flight, one supersedable slot.** A `lint` waiting to be sent is REPLACED by a
 *    newer `lint` — that is what "a stale result is dropped rather than rendered" means in
 *    practice. The evicted promise rejects with {@link AnalyzerError} `reason: 'superseded'`
 *    (see the warning below). `format` and `repair` are NEVER superseded: they are the user
 *    pressing a button, and a keystroke-driven lint must not be able to swallow one.
 *
 * 3. **Sequence numbers route, and they also discard.** Every request carries a monotonic
 *    `seq`; a response whose `seq` is no longer pending — because the request was
 *    superseded, because the service was disposed, or because a replaced Worker is still
 *    talking — is dropped on arrival and never reaches a caller.
 *
 * ═══ ⚠ FOR THE CALLER: SUPERSEDED LINTS REJECT ═══
 *
 * ```ts
 * try { store.publish(await analyzer.lint(text)); }
 * catch (error) { if (!isSuperseded(error)) throw error; }   // a newer lint is already running
 * ```
 *
 * The alternative — leaving the promise pending forever — leaks, and resolving it with the
 * NEWER request's report would hand `DocumentStore` a report for text it did not ask about
 * and it would stamp the wrong `reportVersion` on it. Rejecting is the only option that
 * settles the promise without lying, so it is stated loudly here instead of discovered.
 */

export type AnalyzerFailureReason =
  /** A newer `lint` replaced this one before it was sent. Normal; ignore it. */
  | 'superseded'
  /** `dispose()` was called while this request was outstanding. */
  | 'disposed'
  /** The engine threw. Should be unreachable; the message comes from the engine. */
  | 'engine';
// There is deliberately no `'worker'` member. A Worker that cannot be built, or that dies
// mid-request, is not a failure the CALLER has to know about: the request is re-dispatched
// through the inline transport and answered. A reason nobody can ever observe is a lie in a
// type, so it is not declared.

/** The only rejection `lint`, `format` and `repair` produce. Carries no user text. */
export class AnalyzerError extends Error {
  readonly reason: AnalyzerFailureReason;
  readonly seq: Seq;

  constructor(reason: AnalyzerFailureReason, seq: Seq, message?: string) {
    super(message ?? `analysis ${reason}`);
    this.name = 'AnalyzerError';
    this.reason = reason;
    this.seq = seq;
  }
}

export function isAnalyzerError(value: unknown): value is AnalyzerError {
  return value instanceof AnalyzerError;
}

/** The guard a `DocumentStore` needs: "a newer analysis already owns the answer". */
export function isSuperseded(value: unknown): boolean {
  return isAnalyzerError(value) && value.reason === 'superseded';
}

/**
 * The Worker threshold, in UTF-8 bytes.
 *
 * ⚠ `src/app/config.ts` is the declared single home of every tunable number, and it exports
 * `WORKER_THRESHOLD_BYTES` with this same value. This layer cannot import it: gate 2 denies
 * `@/app/*` from `src/services/**`, and rightly so. So the composition root passes its own
 * number in — `createServices({ analyzer: { workerThresholdBytes: WORKER_THRESHOLD_BYTES } })`
 * — and this constant is only the fallback for a caller that builds the service bare, which
 * in practice means a test. Flagged in the stage report rather than hidden.
 */
export const DEFAULT_WORKER_THRESHOLD_BYTES = 256 * 1024;

export interface AnalyzerOptions {
  /** At or above this many UTF-8 bytes, analysis goes to the Worker. */
  readonly workerThresholdBytes?: number;
  /**
   * Builds the Worker. `null` disables the Worker path outright — every request runs inline,
   * whatever its size. Defaults to the module Worker; a host without `Worker`, or one whose
   * CSP refuses it, falls back to inline on its own.
   */
  readonly createWorker?: (() => Worker) | null;
}

/**
 * The default factory. The `new Worker(new URL(…), { type: 'module' })` spelling is not
 * decoration: it is the exact pattern Vite statically analyses to emit the worker chunk
 * (`vite.config.ts` → `worker: { format: 'es' }`, ADR-0010). Rewriting it as a variable
 * breaks the build silently, at runtime, in production only.
 */
function defaultCreateWorker(): Worker {
  return new Worker(new URL('./worker/analyze.worker.ts', import.meta.url), { type: 'module' });
}

// ── the pending-request bookkeeping ──────────────────────────────────────────────────────

interface Pending {
  readonly request: WorkerRequest;
  readonly settle: (response: WorkerResponse) => void;
  readonly fail: (error: AnalyzerError) => void;
}

/** What a transport is, from the scheduler's point of view. */
interface Transport {
  send(request: WorkerRequest): void;
  dispose(): void;
}

export function createAnalyzerService(options: AnalyzerOptions = {}): AnalyzerService {
  const thresholdBytes = options.workerThresholdBytes ?? DEFAULT_WORKER_THRESHOLD_BYTES;
  const makeWorker =
    options.createWorker === undefined ? defaultCreateWorker : options.createWorker;

  const cache = createAnalysisCache();
  const pending = new Map<Seq, Pending>();

  let nextSeq: Seq = 1;
  let disposed = false;

  // ── the analyzer's own notion of "which document is this" ──────────────────────────────
  //
  // `AnalyzerService` takes a string, not a `DocumentSnapshot`, so the protocol's `version`
  // has to be derived here. Same text ⇒ same version ⇒ the cache hits, which is the whole
  // point of the field: lint → format → repair over one text runs the pipeline once.
  let lastSource: string | null = null;
  let lastVersion: DocumentVersion = 0;

  function versionOf(source: string): DocumentVersion {
    if (lastSource !== source) {
      lastSource = source;
      lastVersion += 1;
    }
    return lastVersion;
  }

  // ── transports ────────────────────────────────────────────────────────────────────────

  function createInlineTransport(): Transport {
    let alive = true;
    return {
      send(request) {
        // A microtask, not synchronous: `lint()` must behave the same on both sides of the
        // 256 KiB line, including "two calls in one tick means the first was superseded".
        queueMicrotask(() => {
          if (!alive) return;
          receive(handleRequest(request, cache));
        });
      },
      dispose() {
        alive = false;
      },
    };
  }

  function createWorkerTransport(worker: Worker): Transport {
    const onMessage = (event: MessageEvent): void => {
      const response: unknown = event.data;
      if (isWorkerResponse(response)) receive(response);
    };
    // A Worker `error` event is a module that would not load, or a top-level throw: the
    // instance is finished, and every request we handed it is lost.
    const onError = (): void => {
      loseWorker();
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);

    return {
      send(request) {
        worker.postMessage(request);
      },
      dispose() {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
        worker.terminate();
      },
    };
  }

  const inline: Transport = createInlineTransport();
  let workerTransport: Transport | null = null;
  /** Set once the Worker has proven unusable here; never retried within one session. */
  let workerUnavailable = makeWorker === null;

  function workerFor(): Transport | null {
    if (workerUnavailable || makeWorker === null) return null;
    if (workerTransport !== null) return workerTransport;
    try {
      workerTransport = createWorkerTransport(makeWorker());
      return workerTransport;
    } catch {
      // No `Worker` global (jsdom, an old embedded webview), a CSP `worker-src` refusal, a
      // blocked blob URL. Inline is slower on a big document, but it is correct, and a
      // correct slow answer beats a broken fast one.
      workerUnavailable = true;
      workerTransport = null;
      return null;
    }
  }

  /**
   * The Worker died. Drop it, and re-dispatch everything it owed us through the inline
   * transport rather than failing requests the user is watching.
   */
  function loseWorker(): void {
    workerUnavailable = true;
    const lost = workerTransport;
    workerTransport = null;
    if (lost !== null) {
      try {
        lost.dispose();
      } catch {
        /* terminating a worker that is already gone */
      }
    }
    if (disposed) return;
    if (inFlight !== null) inline.send(inFlight.request);
  }

  // ── the queue: one in flight, one supersedable lint, a FIFO of user commands ───────────

  let inFlight: Pending | null = null;
  let queuedLint: Pending | null = null;
  const queuedCommands: Pending[] = [];

  function enqueue(entry: Pending): void {
    if (entry.request.kind === 'lint') {
      const displaced = queuedLint;
      queuedLint = entry;
      if (displaced !== null) {
        pending.delete(displaced.request.seq);
        displaced.fail(new AnalyzerError('superseded', displaced.request.seq));
      }
    } else {
      queuedCommands.push(entry);
    }
    pump();
  }

  function pump(): void {
    if (disposed || inFlight !== null) return;
    // Commands first: a click outranks a keystroke.
    const next: Pending | null = queuedCommands.shift() ?? queuedLint;
    if (next === null) return;
    if (next === queuedLint) queuedLint = null;

    inFlight = next;
    const bytes = utf8ByteLength(next.request.source);
    const transport = bytes >= thresholdBytes ? (workerFor() ?? inline) : inline;
    transport.send(next.request);
  }

  function receive(response: WorkerResponse): void {
    const entry = pending.get(response.seq);
    // Rule 3: no pending entry means superseded, disposed, or a Worker we have replaced.
    // The result is dropped here, where it is cheap, rather than rendered.
    if (entry === undefined) return;
    pending.delete(response.seq);
    if (inFlight === entry) inFlight = null;
    entry.settle(response);
    pump();
  }

  function dispatch<T>(
    request: WorkerRequest,
    take: (response: WorkerResponse) => T | undefined,
  ): Promise<T> {
    if (disposed) {
      return Promise.reject(new AnalyzerError('disposed', request.seq));
    }
    return new Promise<T>((resolve, reject) => {
      const entry: Pending = {
        request,
        settle(response) {
          if (response.kind === 'error') {
            reject(new AnalyzerError('engine', response.seq, response.message));
            return;
          }
          const value = take(response);
          if (value === undefined) {
            // A response of the wrong kind for this seq. Unreachable through the protocol;
            // reported rather than resolved with a lie.
            reject(
              new AnalyzerError('engine', response.seq, `unexpected ${response.kind} response`),
            );
            return;
          }
          resolve(value);
        },
        fail: reject,
      };
      pending.set(request.seq, entry);
      enqueue(entry);
    });
  }

  function base(source: string): { seq: Seq; version: DocumentVersion; source: string } {
    const seq = nextSeq;
    nextSeq += 1;
    return { seq, version: versionOf(source), source };
  }

  return {
    lint(source: string, lintOptions?: Partial<LintOptions>): Promise<LintReport> {
      return dispatch(
        {
          kind: 'lint',
          ...base(source),
          ...(lintOptions !== undefined && { options: lintOptions }),
        },
        (response) => (response.kind === 'lint' ? response.report : undefined),
      );
    },

    format(source: string, formatOptions?: Partial<FormatOptions>): Promise<FormatResult> {
      return dispatch(
        {
          kind: 'format',
          ...base(source),
          ...(formatOptions !== undefined && { options: formatOptions }),
        },
        (response) => (response.kind === 'format' ? response.result : undefined),
      );
    },

    repair(source: string, repairOptions?: Partial<RepairOptions>): Promise<RepairResult> {
      return dispatch(
        {
          kind: 'repair',
          ...base(source),
          ...(repairOptions !== undefined && { options: repairOptions }),
        },
        (response) => (response.kind === 'repair' ? response.result : undefined),
      );
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;

      const outstanding = [...pending.values()];
      pending.clear();
      inFlight = null;
      queuedLint = null;
      queuedCommands.length = 0;
      for (const entry of outstanding) {
        entry.fail(new AnalyzerError('disposed', entry.request.seq));
      }

      inline.dispose();
      workerTransport?.dispose();
      workerTransport = null;
      cache.clear();
    },
  };
}

/**
 * Narrowing for whatever comes back over `postMessage`.
 *
 * The mirror of `isWorkerRequest` in `protocol.ts`, and it lives here rather than there for
 * the same reason that one lives there: each side validates what it receives, and neither
 * trusts the shape the other promised.
 */
function isWorkerResponse(value: unknown): value is WorkerResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { kind?: unknown; seq?: unknown };
  return (
    typeof candidate.seq === 'number' &&
    (candidate.kind === 'lint' ||
      candidate.kind === 'format' ||
      candidate.kind === 'repair' ||
      candidate.kind === 'error')
  );
}
